import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import SiteShell from './SiteShell';
import { API_BASE } from '../api/client';

/**
 * Account deletion, reachable from the web without installing the app.
 *
 * Google Play requires this route to exist publicly. Identity is proved with a
 * code sent to the number on the account, so a request cannot be raised
 * against somebody else.
 */
const DeleteAccount = () => {
  const [step, setStep] = useState('mobile');
  const [mobile, setMobile] = useState('');
  const [otp, setOtp] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState('');

  const post = async (path, body) => {
    const res = await fetch(`${API_BASE}/account-deletion/${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    const json = await res.json();
    if (!res.ok) throw new Error(json.message || 'Something went wrong');
    return json;
  };

  const sendCode = async e => {
    e.preventDefault();
    setError('');

    if (!/^\d{10}$/.test(mobile)) {
      setError('Enter the 10-digit mobile number on your account.');
      return;
    }

    setBusy(true);
    try {
      await post('request-otp', { mobile });
      setStep('otp');
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const confirm = async e => {
    e.preventDefault();
    setError('');

    if (!otp.trim()) {
      setError('Enter the code we sent you.');
      return;
    }

    setBusy(true);
    try {
      const res = await post('confirm', { mobile, otp, reason });
      setDone(res.message);
      setStep('done');
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <SiteShell>
      <div className="site-wrap site-legal" style={{ maxWidth: 720 }}>
        <h1>Delete your account</h1>
        <p className="updated">Tarpex.live · account deletion request</p>

        <p>
          You can delete your Tarpex account here, or from <b>Profile → Help &amp; support</b> in
          the app. We will confirm it is you by SMS before anything is removed.
        </p>

        <h2>What gets deleted</h2>
        <ul>
          <li>Your name, email address and profile photo</li>
          <li>Your mobile number, released so it can be registered again</li>
          <li>Your saved addresses and notification tokens</li>
          <li>Your access to the app</li>
        </ul>

        <h2>What we have to keep</h2>
        <p>
          Orders, invoices, payments and GST records are retained for eight years because Indian
          tax and company law require it. They stay linked to your company, not to you
          personally, and the other party to each trade keeps their copy of the invoice — we
          cannot remove that.
        </p>

        <div className="callout">
          {step === 'mobile' && (
            <form onSubmit={sendCode}>
              <b>Step 1 — confirm your number</b>
              <p style={{ margin: '8px 0 14px' }}>
                Enter the mobile number you use to sign in. We will send a verification code.
              </p>
              <input
                className="input"
                inputMode="numeric"
                maxLength={10}
                placeholder="9876543210"
                value={mobile}
                onChange={e => setMobile(e.target.value.replace(/\D/g, ''))}
                style={{ marginBottom: 14 }}
              />
              <button className="site-btn site-btn-primary" type="submit" disabled={busy}>
                {busy ? 'Sending…' : 'Send verification code'}
              </button>
            </form>
          )}

          {step === 'otp' && (
            <form onSubmit={confirm}>
              <b>Step 2 — verify and confirm</b>
              <p style={{ margin: '8px 0 14px' }}>
                If <b>{mobile}</b> has a Tarpex account, we have sent it a code.
              </p>
              <input
                className="input"
                inputMode="numeric"
                maxLength={6}
                placeholder="Verification code"
                value={otp}
                onChange={e => setOtp(e.target.value.replace(/\D/g, ''))}
                style={{ marginBottom: 12 }}
              />
              <textarea
                className="textarea"
                rows={3}
                placeholder="Why are you leaving? (optional)"
                value={reason}
                onChange={e => setReason(e.target.value)}
                style={{ marginBottom: 14 }}
              />
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                <button className="site-btn site-btn-ghost" type="button" onClick={() => setStep('mobile')}>
                  Back
                </button>
                <button className="site-btn site-btn-primary" type="submit" disabled={busy}>
                  {busy ? 'Submitting…' : 'Delete my account'}
                </button>
              </div>
            </form>
          )}

          {step === 'done' && (
            <div>
              <b>Request received</b>
              <p style={{ margin: '8px 0 0' }}>{done}</p>
            </div>
          )}

          {error && (
            <p style={{ color: '#dc2626', marginTop: 12, fontSize: 14.5 }}>{error}</p>
          )}
        </div>

        <h2>Prefer to email?</h2>
        <p>
          Write to <a href="mailto:support@tarpex.live">support@tarpex.live</a> from the address
          on your account and we will handle it within 30 days.
        </p>

        <p style={{ marginTop: 30 }}>
          <Link to="/privacy">Read our privacy policy</Link>
        </p>
      </div>
    </SiteShell>
  );
};

export default DeleteAccount;
